import { createFileRoute, notFound } from "@tanstack/react-router";
import { getFrCalculatorBySlug, getEnCalculatorSlugForFrSlug, getFrCalculatorSlugForEnSlug } from "@/lib/fr-calculators-catalog";
import { getEsCalculatorSlugForEnSlug } from "@/lib/es-calculators-catalog";
import { getDeCalculatorSlugForEnSlug } from "@/lib/de-calculators-catalog";
import { getPtBrCalculatorSlugForEnSlug } from "@/lib/pt-br-calculators-catalog";
import { CALCULATOR_CONTENT } from "@/lib/calculator-content";
import { CalculatorShell } from "@/components/calculators/CalculatorShell";
import { CalculatorRenderer } from "@/components/calculators/CalculatorRenderer";

export const Route = createFileRoute("/fr/calculators/$slug")({
  loader: ({ params }) => {
    const frCalc = getFrCalculatorBySlug(params.slug);
    if (!frCalc) throw notFound();
    const enSlug = getEnCalculatorSlugForFrSlug(frCalc.slug);
    const content = CALCULATOR_CONTENT[enSlug];
    if (!content) throw notFound();
    return { frCalc, enSlug, content };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { frCalc, enSlug } = loaderData;
    const currentCanonical = `https://picamini.online/fr/calculators/${frCalc.slug}`;
    const enUrl = `https://picamini.online/calculators/${enSlug}`;
    const esUrl = `https://picamini.online/es/calculators/${getEsCalculatorSlugForEnSlug(enSlug)}`;
    const deUrl = `https://picamini.online/de/calculators/${getDeCalculatorSlugForEnSlug(enSlug)}`;
    const ptUrl = `https://picamini.online/pt-br/calculators/${getPtBrCalculatorSlugForEnSlug(enSlug)}`;

    return {
      meta: [
        { title: `${frCalc.name} | Picamini.online` },
        { name: "description", content: frCalc.description },
        { name: "keywords", content: frCalc.keywords.join(", ") },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: frCalc.name },
        { property: "og:description", content: frCalc.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: currentCanonical },
        { property: "og:locale", content: "fr_FR" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${frCalc.name} — Calculateur Gratuit sur Picamini.online` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: frCalc.name },
        { name: "twitter:description", content: frCalc.description },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "noindex, follow" },
      ],
      links: [
        { rel: "canonical", href: currentCanonical },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "fr", href: currentCanonical },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: ptUrl },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": frCalc.name,
            "url": currentCanonical,
            "description": frCalc.description,
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "All",
            "inLanguage": "fr",
            "browserRequirements": "Nécessite JavaScript. Nécessite HTML5.",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://picamini.online/fr/" },
              { "@type": "ListItem", "position": 2, "name": "Calculatrices", "item": "https://picamini.online/fr/calculators" },
              { "@type": "ListItem", "position": 3, "name": frCalc.name, "item": currentCanonical },
            ],
          }),
        },
      ],
    };
  },
  component: SingleFrCalculatorPage,
});

function SingleFrCalculatorPage() {
  const { frCalc, enSlug, content } = Route.useLoaderData();

  const calcMetaAdapter = {
    slug: frCalc.slug,
    name: frCalc.name,
    tagline: frCalc.tagline,
    description: frCalc.description,
    category: frCalc.category as any,
    icon: frCalc.icon,
    featured: frCalc.featured,
    keywords: frCalc.keywords,
  };

  return (
    <CalculatorShell calc={calcMetaAdapter} content={content} lang="fr">
      <CalculatorRenderer slug={enSlug} />
    </CalculatorShell>
  );
}
